import { useDialog } from '@repo/ui';
import { useCallback, useEffect, useState } from 'react';
import { fetchQuery, useMutation, useRelayEnvironment } from 'react-relay';

import { snapshotGetQuery } from '@/__generated__/snapshotGetQuery.graphql';
import { snapshotSaveMutation } from '@/__generated__/snapshotSaveMutation.graphql';
import { getSnapshot, saveSnapshot } from '@/graphql/query/tax-refund/snapshot';
import { CURRENT_APPLY_RESULT_TERMPLATE_VERSION, CURRENT_LOOKUP_RESULT_TERMPLATE_VERSION } from '@/lib/constants/variables';

export const useSnapshotData = (type: 'lookup' | 'apply', snapshotCode?: string) => {
  const [snapshotData, setSnapshotData] = useState<any>(undefined);
  const [commit] = useMutation<snapshotSaveMutation>(saveSnapshot);
  const environment = useRelayEnvironment();
  const { showDialog } = useDialog();

  const saveSnapshotData = useCallback(
    (refundId: string, displayData: any) => {
      commit({
        variables: {
          type,
          refundId,
          snapshotData: JSON.stringify({
            displayData,
            displaySize: {
              width: window.innerWidth,
              height: window.innerHeight,
              scrollTop: window.scrollY
            },
            templateVersion: type === 'apply' ? CURRENT_APPLY_RESULT_TERMPLATE_VERSION : CURRENT_LOOKUP_RESULT_TERMPLATE_VERSION
          })
        }
      });
    },
    [commit, type]
  );

  const getSnapshotData = useCallback(async () => {
    if (!snapshotCode) return null;
    const response = await fetchQuery<snapshotGetQuery>(environment, getSnapshot, {
      type,
      snapshotCode: snapshotCode
    })
      .toPromise()
      .catch(() => {
        return null;
      });

    if (response && !response?.getApplyRefundCertificate.result) {
      showDialog({
        title: '스냅샷 데이터를 불러오는 중 오류가 발생했어요.',
        description: '윈도우를 닫고 브릭스에서 다시 시도해주세요.'
      });
    } else if (response?.getApplyRefundCertificate.snapshotData) {
      const parsedData = JSON.parse(response.getApplyRefundCertificate.snapshotData);
      setSnapshotData(parsedData);
      return parsedData;
    }

    setSnapshotData(null);
    return null;
  }, [environment, showDialog, snapshotCode, type]);

  useEffect(() => {
    if (!snapshotCode) {
      setSnapshotData(null);
      return;
    }
    getSnapshotData();
  }, [getSnapshotData, snapshotCode]);

  return { saveSnapshotData, getSnapshotData, snapshotData };
};
